/**
 * Locale registry (docs/I18N.md). Every locale the app will ever speak is listed here; `status` says whether it is
 * shipped ('available': UI + content must be complete, `npm run i18n:check` fails on a gap) or planned for a later
 * wave ('planned': may be partial, falls back along `fallback`, hidden from the pickers). Adding a wave = flip the
 * status and fill the dictionaries/catalogs; nothing else needs refactoring.
 */
export const LOCALE_IDS = ['en', 'vi', 'zh-Hans', 'es', 'pt-BR', 'ja', 'ko', 'id', 'fr', 'de', 'zh-Hant'] as const;
export type Locale = (typeof LOCALE_IDS)[number];

export type ScriptFamily = 'latin' | 'cjk-sc' | 'cjk-tc' | 'cjk-jp' | 'cjk-kr';

export interface LocaleInfo {
  id: Locale;
  /** Name in its own language (pickers always show native names). */
  native: string;
  english: string;
  status: 'available' | 'planned';
  /** BCP 47 tag for Intl (numbers, dates, plurals, collation). */
  intl: string;
  /** Web Speech `lang` for the teacher voice; `voiceHints` = preferred voice-name fragments. */
  tts: string;
  voiceHints?: string[];
  /** Web Speech fallback rate (when no pre-rendered clip exists); default 0.9. */
  ttsRate?: number;
  /** Fallback chain after the locale itself (always ends with 'en'). */
  fallback: Locale[];
  /** Font stack family (apps/client styles/i18n.css) + line-height tweaks for CJK. */
  script: ScriptFamily;
  /** Browser / device tags that resolve to this locale (lower-case prefixes, most specific first). */
  match: string[];
}

export const LOCALES: Record<Locale, LocaleInfo> = {
  en: { id: 'en', native: 'English', english: 'English', status: 'available', intl: 'en-US', tts: 'en-US', voiceHints: ['samantha', 'female', 'google us'], fallback: [], script: 'latin', match: ['en'] },
  vi: { id: 'vi', native: 'Tiếng Việt', english: 'Vietnamese', status: 'available', intl: 'vi-VN', tts: 'vi-VN', voiceHints: ['linh', 'google'], ttsRate: 0.85, fallback: ['en'], script: 'latin', match: ['vi'] },
  'zh-Hans': {
    id: 'zh-Hans',
    native: '简体中文',
    english: 'Chinese (Simplified)',
    status: 'available',
    intl: 'zh-CN',
    tts: 'zh-CN',
    voiceHints: ['tingting', 'ting-ting', 'xiaoxiao', 'google 普通话', 'mandarin'],
    fallback: ['en'],
    script: 'cjk-sc',
    match: ['zh-hans', 'zh-cn', 'zh-sg', 'zh-my', 'zh'],
  },
  es: { id: 'es', native: 'Español', english: 'Spanish', status: 'planned', intl: 'es-ES', tts: 'es-ES', fallback: ['en'], script: 'latin', match: ['es'] },
  'pt-BR': { id: 'pt-BR', native: 'Português (Brasil)', english: 'Portuguese (Brazil)', status: 'planned', intl: 'pt-BR', tts: 'pt-BR', fallback: ['en'], script: 'latin', match: ['pt-br', 'pt'] },
  ja: { id: 'ja', native: '日本語', english: 'Japanese', status: 'planned', intl: 'ja-JP', tts: 'ja-JP', fallback: ['en'], script: 'cjk-jp', match: ['ja'] },
  ko: { id: 'ko', native: '한국어', english: 'Korean', status: 'planned', intl: 'ko-KR', tts: 'ko-KR', fallback: ['en'], script: 'cjk-kr', match: ['ko'] },
  id: { id: 'id', native: 'Bahasa Indonesia', english: 'Indonesian', status: 'planned', intl: 'id-ID', tts: 'id-ID', fallback: ['en'], script: 'latin', match: ['id', 'in'] },
  fr: { id: 'fr', native: 'Français', english: 'French', status: 'planned', intl: 'fr-FR', tts: 'fr-FR', fallback: ['en'], script: 'latin', match: ['fr'] },
  de: { id: 'de', native: 'Deutsch', english: 'German', status: 'planned', intl: 'de-DE', tts: 'de-DE', fallback: ['en'], script: 'latin', match: ['de'] },
  'zh-Hant': {
    id: 'zh-Hant',
    native: '繁體中文',
    english: 'Chinese (Traditional)',
    status: 'planned',
    intl: 'zh-TW',
    tts: 'zh-TW',
    fallback: ['zh-Hans', 'en'],
    script: 'cjk-tc',
    match: ['zh-hant', 'zh-tw', 'zh-hk', 'zh-mo'],
  },
};

export const ALL_LOCALES: Locale[] = [...LOCALE_IDS];
/** Shipped locales (pickers, completeness gates). */
export const AVAILABLE_LOCALES: Locale[] = LOCALE_IDS.filter((l) => LOCALES[l].status === 'available');
export const PLANNED_LOCALES: Locale[] = LOCALE_IDS.filter((l) => LOCALES[l].status === 'planned');
export const DEFAULT_LOCALE: Locale = 'en';

export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALE_IDS as readonly string[]).includes(v);
export const isAvailableLocale = (v: unknown): v is Locale => isLocale(v) && LOCALES[v].status === 'available';

/** The locale itself, then its fallbacks, ending with 'en' (e.g. zh-Hant → zh-Hans → en, pt-BR → en). */
export function fallbackChain(locale: Locale): Locale[] {
  const out: Locale[] = [locale];
  for (const f of LOCALES[locale]?.fallback ?? []) if (!out.includes(f)) out.push(f);
  if (!out.includes('en')) out.push('en');
  return out;
}

/** Map one BCP 47 tag (any case, `_` or `-`) to a registry locale, or undefined. Script subtags win over regions. */
export function matchLocale(tag: string | null | undefined): Locale | undefined {
  if (!tag) return undefined;
  const t = tag.trim().replace(/_/g, '-').toLowerCase();
  if (!t) return undefined;
  if (t.startsWith('zh')) {
    if (/-hant\b/.test(t) || /-(tw|hk|mo)\b/.test(t)) return 'zh-Hant';
    return 'zh-Hans';
  }
  let best: { l: Locale; len: number } | undefined;
  for (const l of LOCALE_IDS)
    for (const m of LOCALES[l].match) if ((t === m || t.startsWith(`${m}-`)) && (!best || m.length > best.len)) best = { l, len: m.length };
  return best?.l;
}

/**
 * Pick the app locale from the device / browser preferences (navigator.languages, Accept-Language order):
 * the first tag whose locale — or one of its fallbacks — is available. Defaults to English.
 */
export function resolveLocale(tags: readonly (string | null | undefined)[] | string | null | undefined, available: readonly Locale[] = AVAILABLE_LOCALES): Locale {
  const list = typeof tags === 'string' ? tags.split(',').map((x) => x.split(';')[0]!) : (tags ?? []);
  for (const tag of list) {
    const l = matchLocale(tag);
    if (!l) continue;
    const hit = fallbackChain(l).find((x) => available.includes(x) && x !== 'en');
    if (hit) return hit;
    if (l === 'en') return 'en';
  }
  return DEFAULT_LOCALE;
}

/** Normalise any stored / incoming value (old 'vi' / 'en', 'zh-CN', 'zh_Hans', …) to an available locale. */
export function normalizeLocale(v: unknown, available: readonly Locale[] = AVAILABLE_LOCALES): Locale {
  if (isLocale(v) && available.includes(v)) return v;
  return typeof v === 'string' ? resolveLocale([v], available) : DEFAULT_LOCALE;
}

export const intlLocale = (l: Locale, overrides?: Partial<Record<Locale, string>>) => overrides?.[l] ?? LOCALES[l]?.intl ?? 'en-US';
export const isCjk = (l: Locale) => LOCALES[l]?.script.startsWith('cjk') ?? false;

/** Shipped locales as a literal type (kept equal to AVAILABLE_LOCALES by test/i18n.test.ts). */
export type AvailableLocale = 'en' | 'vi' | 'zh-Hans';
/** A small per-locale table: every available locale is required (a gap = type error), planned ones optional. */
export type LocaleTable<T> = Record<AvailableLocale, T> & Partial<Record<Locale, T>>;
/** Value for a locale along its fallback chain. */
export function pickLocale<T>(table: Partial<Record<Locale, T>> & { en: T }, lang: Locale): T {
  for (const l of fallbackChain(lang)) {
    const v = table[l];
    if (v !== undefined) return v;
  }
  return table.en;
}

/** "a, b and c" in the locale (Intl.ListFormat; plain fallback). */
export function joinList(items: readonly string[], lang: Locale): string {
  if (items.length <= 1) return items[0] ?? '';
  try {
    return new Intl.ListFormat(intlLocale(lang, { en: 'en-GB' }), { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
  }
}
